import React, { RefObject, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CalendarMonth } from './CalendarMonth';
import { TimelineDayTasksModal } from './TimelineDayTasksModal';
import { useCalendarInfiniteScroll } from './useCalendarInfiniteScroll';
import { useTimelineView } from './TimelineViewContext';

const getWeekdays = (language: string) => Array.from({ length: 7 }, (_, index) => (
    new Date(2024, 0, index + 1).toLocaleDateString(language, { weekday: 'short' })
));

interface TimelineDaysCalendarProps {
    onActivePeriodChange: (label: string, date: Date) => void;
    scrollContainerRef: RefObject<HTMLDivElement>;
}

export const TimelineDaysCalendar: React.FC<TimelineDaysCalendarProps> = ({
    onActivePeriodChange, scrollContainerRef,
}) => {
    const { i18n } = useTranslation();
    const {
        onCreateFlexibleTask, onRequestTaskListReturn, onScaleChange, onTaskListOpened, selectedDate,
        taskListToOpen, tasksWithDueDates,
    } = useTimelineView();
    const [openDate, setOpenDate] = useState<Date | null>(null);
    const [hoveredTaskId, setHoveredTaskId] = useState<string | null>(null);
    const { activeDate, activeLabel, periods, reset } = useCalendarInfiniteScroll(
        scrollContainerRef, selectedDate, 'day'
    );
    const weekdays = useMemo(() => getWeekdays(i18n.language), [i18n.language]);
    useEffect(() => {
        reset();
    }, [reset, selectedDate]);
    useEffect(() => {
        if (taskListToOpen?.scope !== 'day') return;
        setOpenDate(taskListToOpen.date);
        onTaskListOpened();
    }, [onTaskListOpened, taskListToOpen]);
    useEffect(() => {
        const fallback = selectedDate.toLocaleDateString(i18n.language, {
            month: 'long', year: 'numeric',
        });
        onActivePeriodChange(activeLabel || fallback, activeDate || selectedDate);
    }, [activeDate, activeLabel, i18n.language, onActivePeriodChange, selectedDate]);
    const handleDayOpen = (date: Date) => {
        setOpenDate(date);
    };
    return (
        <div className="calendar-view timeline-days-calendar">
            <div className="calendar-infinite-list">
                {periods.map(({ date, offset }) => (
                    <CalendarMonth key={`${date.getFullYear()}-${date.getMonth()}`}
                        current={offset === 0} date={date} tasks={tasksWithDueDates}
                        weekdays={weekdays} onCreateTask={(day) => onCreateFlexibleTask(day, 'day')}
                        onOpenDay={handleDayOpen} onOpenDayTimeline={(day) => onScaleChange('day', day)}
                        onOpenYearTimeline={(month) => onScaleChange('year', month)} onOpenTask={() => undefined}
                        hoveredTaskId={hoveredTaskId} onTaskHoverChange={setHoveredTaskId} />
                ))}
            </div>
            {openDate && <TimelineDayTasksModal date={openDate} onClose={() => setOpenDate(null)}
                onPrepareAction={() => {
                    onRequestTaskListReturn('day', openDate);
                    setOpenDate(null);
                }} />}
        </div>
    );
};
